import { useDebounce } from '@helpers/hooks/use-debounce/useDebounce';
import { usePublicLists } from '@terminal/react-query-hooks/usePublicLists';
import { searchWatchlists } from '@terminal/services/watchlist/watchlist';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MentionWatchlistCandidate } from './mention-source-types';

const SEARCH_DEBOUNCE_MS = 300;

export interface UseMentionWatchlistSourceResult {
  setQuery: (query: string) => void;
  clear: () => void;
  loading: boolean;
  lists: MentionWatchlistCandidate[];
  publicLists: MentionWatchlistCandidate[];
}

interface SearchResults {
  lists: MentionWatchlistCandidate[];
  publicLists: MentionWatchlistCandidate[];
}

const searchWatchlistCandidates = async (
  query: string,
  signal: AbortSignal
): Promise<SearchResults | null | undefined> => {
  try {
    const { data, error } = await searchWatchlists({ q: query }, signal);
    if (error || !data) {
      return null;
    }

    const lists: MentionWatchlistCandidate[] = [];
    const publicLists: MentionWatchlistCandidate[] = [];

    for (const result of data.results) {
      const candidate: MentionWatchlistCandidate = {
        kind: result.type,
        id: result.id,
        label: result.name,
      };
      if (result.type === 'public-list') {
        publicLists.push(candidate);
      } else {
        lists.push(candidate);
      }
    }

    return { lists, publicLists };
  } catch {
    return signal.aborted ? undefined : null;
  }
};

export const useMentionWatchlistSource = (
  enabled = true
): UseMentionWatchlistSourceResult => {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const [loading, setLoading] = useState(false);
  const [searchLists, setSearchLists] = useState<MentionWatchlistCandidate[]>(
    []
  );
  const [searchPublicLists, setSearchPublicLists] = useState<
    MentionWatchlistCandidate[]
  >([]);
  const latestQueryRef = useRef('');
  const activeSearchControllerRef = useRef<AbortController | null>(null);

  const privateLists = useWatchlist(state => state.lists);
  const { data: publicListsData, isLoading: publicListsLoading } =
    usePublicLists(enabled);

  useEffect(() => {
    const trimmed = debouncedQuery.trim();
    if (!trimmed) {
      setSearchLists([]);
      setSearchPublicLists([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    activeSearchControllerRef.current = controller;
    setLoading(true);

    const search = async () => {
      const results = await searchWatchlistCandidates(
        trimmed,
        controller.signal
      );

      if (
        !controller.signal.aborted &&
        latestQueryRef.current === debouncedQuery
      ) {
        if (results) {
          setSearchLists(results.lists);
          setSearchPublicLists(results.publicLists);
        } else {
          setSearchLists([]);
          setSearchPublicLists([]);
        }
        setLoading(false);
      }
    };

    void search();

    return () => {
      controller.abort();
      if (activeSearchControllerRef.current === controller) {
        activeSearchControllerRef.current = null;
      }
    };
  }, [debouncedQuery]);

  const updateQuery = useCallback((nextQuery: string) => {
    latestQueryRef.current = nextQuery;
    setQuery(nextQuery);
  }, []);

  const clear = useCallback(() => {
    updateQuery('');
    activeSearchControllerRef.current?.abort();
    activeSearchControllerRef.current = null;
    setSearchLists([]);
    setSearchPublicLists([]);
    setLoading(false);
  }, [updateQuery]);

  const normalizedQuery = query.trim();

  const lists = useMemo<MentionWatchlistCandidate[]>(() => {
    if (normalizedQuery) {
      return searchLists;
    }
    return privateLists.map(list => ({
      kind: 'list' as const,
      id: list.id,
      label: list.name,
    }));
  }, [normalizedQuery, privateLists, searchLists]);

  const publicLists = useMemo<MentionWatchlistCandidate[]>(() => {
    if (normalizedQuery) {
      return searchPublicLists;
    }
    return (publicListsData ?? []).map(list => ({
      kind: 'public-list' as const,
      id: list.id,
      label: list.name,
    }));
  }, [normalizedQuery, publicListsData, searchPublicLists]);

  const debouncing = debouncedQuery !== query;

  return {
    setQuery: updateQuery,
    clear,
    loading: normalizedQuery ? loading || debouncing : publicListsLoading,
    lists,
    publicLists,
  };
};
